import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, MapPin, Clock, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabaseClient';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    industry: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.industry || !formData.message) {
      toast.error('Please fill in all fields');
      return;
    }

    if (!formData.email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      // 1. Insert contact submission to Supabase
      const { error: dbError } = await supabase
        .from('contact_submissions')
        .insert({
          name: formData.name,
          email: formData.email,
          industry: formData.industry,
          message: formData.message
        });

      if (dbError) {
        console.error('Database storage failed:', dbError);
        // We continue anyway to try sending the email
      }

      // 2. Trigger email notification via Edge Function
      const { error: emailError } = await supabase.functions.invoke('send-email', {
        body: {
          to: 'hello@lucentsky.com.au',
          template: 'contact_submission',
          data: {
            name: formData.name,
            email: formData.email,
            industry: formData.industry,
            message: formData.message
          }
        }
      });

      if (emailError) {
        console.error('Email notification failed:', emailError);
        // If BOTH failed, then we show an error
        if (dbError) {
          toast.error('There was a problem sending your message. Please try again or email us directly.');
          setLoading(false);
          return;
        }
      }

      setSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Could not send message. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F8F9FA] via-[#EDF6F9] to-[#E2E8F0] flex items-center justify-center py-12 px-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white/80 backdrop-blur-xl border border-white/20 rounded-2xl p-12 max-w-2xl text-center shadow-xl"
          data-testid="contact-success"
        >
          <div className="w-20 h-20 bg-gradient-to-br from-[#74A57F] to-[#2A9D8F] rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="w-10 h-10 text-white" />
          </div>

          <h2 className="text-4xl font-bold text-[#003B5C] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Message Received!
          </h2>

          <p className="text-xl text-slate-600 mb-6">
            Thank you for reaching out. We typically respond to all local enquiries within 24 hours.
          </p>

          <p className="text-slate-600 mb-8">
            We've received your message and will get back to you at <strong>{formData.email}</strong> shortly.
          </p>

          <Button
            onClick={() => window.location.href = '/'}
            className="bg-[#003B5C] text-white hover:bg-[#002A42] rounded-full px-8 py-6 text-lg font-medium"
            data-testid="back-home"
          >
            Back to Home
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="contact-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#F8F9FA] via-[#EDF6F9] to-[#E2E8F0] py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-bold text-[#003B5C] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Get in Touch
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              Have questions about ESG tracking or grants? We're here to help Australian businesses succeed.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="bg-white rounded-2xl border border-slate-100 p-8 shadow-sm">
                <h2 className="text-2xl font-semibold text-[#003B5C] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Send us a Message
                </h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="name" className="text-[#003B5C] mb-2 block">
                      Full Name *
                    </Label>
                    <Input
                      id="name"
                      type="text"
                      placeholder="John Smith"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="h-12"
                      data-testid="contact-name-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-[#003B5C] mb-2 block">
                      Email Address *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="john@company.com.au"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="h-12"
                      data-testid="contact-email-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="industry" className="text-[#003B5C] mb-2 block">
                      Industry *
                    </Label>
                    <Select value={formData.industry} onValueChange={(value) => setFormData({ ...formData, industry: value })}>
                      <SelectTrigger className="h-12" data-testid="contact-industry-select">
                        <SelectValue placeholder="Select your industry" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="agriculture">Agriculture / Primary Production</SelectItem>
                        <SelectItem value="manufacturing">Manufacturing</SelectItem>
                        <SelectItem value="tourism">Tourism / Hospitality</SelectItem>
                        <SelectItem value="construction">Construction</SelectItem>
                        <SelectItem value="professional">Professional Services</SelectItem>
                        <SelectItem value="retail">Retail / E-commerce</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-[#003B5C] mb-2 block">
                      Message *
                    </Label>
                    <Textarea
                      id="message"
                      placeholder="Tell us about your ESG tracking needs or questions..."
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="min-h-[150px]"
                      data-testid="contact-message-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#003B5C] text-white hover:bg-[#002A42] rounded-full h-14 text-lg font-medium"
                    data-testid="contact-submit-button"
                  >
                    {loading ? 'Sending...' : 'Send Message'}
                    <Send className="w-5 h-5 ml-2" />
                  </Button>
                </form>
              </div>
            </motion.div>

            {/* Contact Info & Map */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="space-y-8"
            >
              {/* Contact Information */}
              <div className="bg-white rounded-2xl border border-slate-100 p-8 shadow-sm">
                <h3 className="text-xl font-semibold text-[#003B5C] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Contact Information
                </h3>

                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#EDF6F9] rounded-lg flex items-center justify-center">
                      <Mail className="w-6 h-6 text-[#003B5C]" />
                    </div>
                    <div>
                      <div className="font-medium text-[#003B5C] mb-1">Email</div>
                      <a href="mailto:hello@lucentsky.com.au" className="text-slate-600 hover:text-[#003B5C] transition-colors">
                        hello@lucentsky.com.au
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#EDF6F9] rounded-lg flex items-center justify-center">
                      <MapPin className="w-6 h-6 text-[#003B5C]" />
                    </div>
                    <div>
                      <div className="font-medium text-[#003B5C] mb-1">Location</div>
                      <div className="text-slate-600">
                        Doonan, Sunshine Coast<br />
                        Queensland, Australia
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#EDF6F9] rounded-lg flex items-center justify-center">
                      <Clock className="w-6 h-6 text-[#003B5C]" />
                    </div>
                    <div>
                      <div className="font-medium text-[#003B5C] mb-1">Response Time</div>
                      <div className="text-slate-600">
                        Typically within 24 hours for local enquiries
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Support Promise */}
              <div className="bg-gradient-to-br from-[#003B5C] to-[#74A57F] rounded-2xl p-8 text-white shadow-xl">
                <h3 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Our Support Promise
                </h3>
                <p className="text-white/90 leading-relaxed mb-4">
                  Need help with a formula? Not sure which grant fits? We typically respond to all local enquiries within 24 hours.
                </p>
                <p className="text-white/80 text-sm">
                  Australian businesses supporting Australian businesses—that's the Lucent Sky way.
                </p>
              </div>

              {/* Map Section */}
              <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm" data-testid="map-section">
                <div className="h-80 relative">
                  <iframe
                    title="Lucent Sky ESG Location - Doonan, Sunshine Coast"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d56598.89!2d153.0!3d-26.4!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6b9371e77b8b8b8b%3A0x0!2sDoonan%20QLD!5e0!3m2!1sen!2sau!4v1234567890"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="grayscale hover:grayscale-0 transition-all duration-300"
                  ></iframe>
                  <div className="absolute bottom-4 left-4 bg-white px-4 py-2 rounded-lg shadow-lg">
                    <p className="text-sm font-medium text-[#003B5C]">📍 Sunshine Coast, QLD</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
